import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Button,
  MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import DescriptionIcon from '@material-ui/icons/Description';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import SportsIcon from '@material-ui/icons/EmojiEvents';
import HotelIcon from '@material-ui/icons/Hotel';
import LibraryMusicIcon from '@material-ui/icons/LibraryMusic';
import * as XLSX from 'xlsx-js-style';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { toastr } from 'react-redux-toastr';
import GeneralGrid from 'common/components/layout/grid/GeneralGrid';
import LoadingProgress from 'common/components/progress/loading';
import { BASE_API, USER_TOKEN } from 'config/consts';
import SINDILEGIS_LOGO from './sindilegisLogo';

const REPORT_OPTIONS = [
  { value: 'geral', label: 'Relatório Geral' },
  { value: 'quartos', label: 'Relatório de Quartos' },
  { value: 'modalidades', label: 'Relatório de Modalidades Esportivas' },
  { value: 'sarau', label: 'Relatório do Sarau' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'confirmed', label: 'Confirmada' },
  { value: 'pending_payment', label: 'Pendente de pagamento' },
];

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '90vw',
    paddingBottom: '4rem',
  },
  header: {
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2),
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1a3a5c',
  },
  subtitle: {
    fontSize: 14,
    color: '#666',
    marginTop: 4,
  },
  filtersCard: {
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  filtersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: theme.spacing(2),
    alignItems: 'end',
  },
  statsContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
  },
  statCard: {
    padding: theme.spacing(2.5),
    borderRadius: 12,
    flex: '1 1 200px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: 12,
    color: '#888',
  },
  sectionHeader: {
    marginBottom: theme.spacing(1.5),
  },
  searchBox: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  chipPagante: {
    backgroundColor: '#fff3e0',
    color: '#e65100',
    fontSize: 11,
    height: 24,
  },
  chipGratuito: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontSize: 11,
    height: 24,
  },
  actionButton: {
    minHeight: 40,
    backgroundColor: '#1a3a5c',
    color: '#fff',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(6),
  },
  emptyIcon: {
    fontSize: 64,
    color: '#ccc',
    marginBottom: theme.spacing(2),
  },
  emptyText: {
    color: '#999',
    fontSize: 16,
  },
  emptySubtext: {
    color: '#bbb',
    fontSize: 13,
    marginTop: 4,
  },
}));

const getHeaders = () => ({
  Authorization: `${JSON.parse(localStorage.getItem(USER_TOKEN))}`,
});

const sanitizeFilename = (value) =>
  `${value || 'arquivo'}`
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_]/g, '-')
    .replace(/-+/g, '-')
    .toLowerCase();

const formatCurrency = (value) => {
  const parsed = Number(value || 0);
  if (!parsed) return '';
  return parsed.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const formatDate = (value, includeTime = false) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = `${date.getDate()}`.padStart(2, '0');
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const year = date.getFullYear();
  if (!includeTime) return `${day}/${month}/${year}`;
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const parseBirthday = (birthday) => {
  if (!birthday) return null;
  if (typeof birthday === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(birthday)) {
    return new Date(`${birthday}T00:00:00`);
  }
  if (typeof birthday === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(birthday)) {
    const [day, month, year] = birthday.split('/');
    return new Date(`${year}-${month}-${day}T00:00:00`);
  }
  const date = new Date(birthday);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getAge = (birthday) => {
  const date = parseBirthday(birthday);
  if (!date) return '';
  return new Date().getFullYear() - date.getFullYear();
};

const arrayify = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined || value === '') return [];
  return [value];
};

const isSelected = (value) => value === true || value === 'true' || value === 1 || value === '1';

const getStatusLabel = (status) => {
  switch (status) {
    case 'confirmed':
      return 'Confirmada';
    case 'pending_payment':
      return 'Pendente de pagamento';
    case 'cancelled':
      return 'Cancelada';
    default:
      return 'Rascunho';
  }
};

const getStatusChipStyle = (status) => {
  if (status === 'confirmed') {
    return {
      backgroundColor: '#e8f5e9',
      color: '#2e7d32',
    };
  }
  if (status === 'pending_payment') {
    return {
      backgroundColor: '#fff3e0',
      color: '#e65100',
    };
  }
  return {
    backgroundColor: '#eceff1',
    color: '#455a64',
  };
};

const getCorridaCategoryLabel = (birthday) => {
  const age = Number(getAge(birthday));
  if (!Number.isFinite(age)) return 'Corrida 5 km';
  if (age <= 40) return 'Corrida 5 km — até 40 anos';
  if (age <= 45) return 'Corrida 5 km — 41 a 45 anos';
  if (age <= 50) return 'Corrida 5 km — 46 a 50 anos';
  if (age <= 55) return 'Corrida 5 km — 51 a 55 anos';
  if (age <= 60) return 'Corrida 5 km — 56 a 60 anos';
  if (age <= 65) return 'Corrida 5 km — 61 a 65 anos';
  if (age <= 70) return 'Corrida 5 km — 66 a 70 anos';
  return 'Corrida 5 km — acima de 70 anos';
};

const getSportsSummary = (modalities = {}, birthday) => {
  const items = [];

  if (isSelected(modalities.corrida)) {
    const shirt = modalities.corrida_shirt_size
      ? ` | Camisa: ${modalities.corrida_shirt_size}`
      : '';
    items.push(`${getCorridaCategoryLabel(birthday)}${shirt}`);
  }

  if (isSelected(modalities.tenis)) {
    const categories = arrayify(modalities.tenis_categories).filter(Boolean);
    items.push(
      categories.length > 0 ? `Tênis de Campo: ${categories.join(', ')}` : 'Tênis de Campo'
    );
  }

  if (isSelected(modalities.domino)) {
    items.push(
      `Dominó${
        modalities.domino_partner_name ? ` | Parceiro: ${modalities.domino_partner_name}` : ''
      }`
    );
  }

  if (isSelected(modalities.beach_tennis)) {
    const categories = arrayify(modalities.beach_tennis_categories).filter(Boolean);
    const partnerByCategory =
      modalities.beach_tennis_partner_name &&
      typeof modalities.beach_tennis_partner_name === 'object' &&
      !Array.isArray(modalities.beach_tennis_partner_name)
        ? modalities.beach_tennis_partner_name
        : null;
    if (categories.length > 0) {
      const parts = categories.map((cat) => {
        const partner = partnerByCategory && partnerByCategory[cat];
        return partner ? `${cat} (Parceiro: ${partner})` : cat;
      });
      items.push(`Beach Tennis: ${parts.join(', ')}`);
    } else {
      items.push('Beach Tennis');
    }
  }

  if (isSelected(modalities.futebol)) {
    items.push('Futebol');
  }

  if (isSelected(modalities.volei)) {
    items.push('Vôlei de Praia 4x4');
  }

  if (items.length === 0) {
    if (isSelected(modalities.nao_participar_esportes)) {
      return ['Não participará de modalidades esportivas'];
    }
    return ['Nenhuma modalidade esportiva selecionada'];
  }

  return items;
};

const getSarauSummary = (sarauActivities = {}) => {
  const items = [];

  if (isSelected(sarauActivities.artes_visuais)) items.push('Artes Visuais');
  if (isSelected(sarauActivities.literatura)) items.push('Literatura');
  if (isSelected(sarauActivities.artes_cenicas)) items.push('Artes Cênicas');
  if (isSelected(sarauActivities.musica)) items.push('Música');
  if (isSelected(sarauActivities.danca)) items.push('Dança');
  if (isSelected(sarauActivities.outros)) {
    items.push(
      sarauActivities.outros_details ? `Outros: ${sarauActivities.outros_details}` : 'Outros'
    );
  }

  if (items.length === 0) {
    if (isSelected(sarauActivities.nao_participar_sarau)) return ['Não participará do Sarau'];
    return ['Nenhuma atividade de Sarau selecionada'];
  }

  return items;
};

const buildTermsSummary = (termsAccepted = {}, companions = []) => {
  const hasMinor = Array.isArray(companions) && companions.some((companion) => companion?.is_minor);
  const normalizedTerms = Array.isArray(termsAccepted)
    ? termsAccepted.reduce((acc, item) => ({ ...acc, [item]: true }), {})
    : termsAccepted || {};

  return [
    {
      field: 'Declaro estar apto(a) fisicamente para participar das atividades esportivas',
      value: normalizedTerms.aptidao_fisica ? 'Sim' : 'Não',
    },
    {
      field: 'Estou ciente e concordo com as regras da organização',
      value: normalizedTerms.regras_organizacao ? 'Sim' : 'Não',
    },
    {
      field: 'Autorizo o uso de imagem em materiais institucionais do Sindilegis',
      value: normalizedTerms.uso_imagem ? 'Sim' : 'Não',
    },
    {
      field: 'Sou responsável legal pelo menor inscrito (quando aplicável)',
      value: hasMinor
        ? normalizedTerms.responsavel_menor || normalizedTerms.responsavel_legal_menor
          ? 'Sim'
          : 'Não informado'
        : 'Não se aplica',
    },
    {
      field:
        'Autorizo o Sindilegis a coletar, armazenar e tratar meus dados pessoais conforme a Lei 13.709/2018',
      value: normalizedTerms.lgpd ? 'Sim' : 'Não',
    },
  ];
};

const buildSheetData = (rows = [], columns = []) => {
  const headers = columns.length > 0 ? columns : rows.length > 0 ? Object.keys(rows[0]) : [];
  return [headers, ...rows.map((row) => headers.map((header) => row?.[header] ?? ''))];
};

const EXCEL_HEADER_STYLE = {
  font: {
    bold: true,
    color: { rgb: 'FFFFFF' },
    sz: 11,
  },
  fill: {
    patternType: 'solid',
    fgColor: { rgb: '2F6B58' },
  },
  alignment: {
    horizontal: 'center',
    vertical: 'center',
    wrapText: true,
  },
  border: {
    top: { style: 'thin', color: { rgb: '1F4E3D' } },
    bottom: { style: 'thin', color: { rgb: '1F4E3D' } },
    left: { style: 'thin', color: { rgb: '1F4E3D' } },
    right: { style: 'thin', color: { rgb: '1F4E3D' } },
  },
};

const EXCEL_BODY_STYLE = {
  font: {
    color: { rgb: '1F2937' },
    sz: 10,
  },
  alignment: {
    vertical: 'center',
    wrapText: true,
  },
  border: {
    top: { style: 'thin', color: { rgb: 'D1D5DB' } },
    bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
    left: { style: 'thin', color: { rgb: 'D1D5DB' } },
    right: { style: 'thin', color: { rgb: 'D1D5DB' } },
  },
};

const EXCEL_ALT_ROW_STYLE = {
  ...EXCEL_BODY_STYLE,
  fill: {
    patternType: 'solid',
    fgColor: { rgb: 'F7FBF9' },
  },
};

const getExcelColumnWidth = (header, rows = []) => {
  const maxValueLength = rows.reduce((acc, row) => {
    const value = row?.[header];
    const normalizedValue = value === null || value === undefined ? '' : `${value}`;
    return Math.max(acc, normalizedValue.length);
  }, `${header || ''}`.length);

  return Math.min(Math.max(maxValueLength + 4, 14), 42);
};

const buildStyledWorksheet = (rows = [], columns = []) => {
  const data = buildSheetData(rows, columns);
  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const headers = data[0] || [];
  const lastCellAddress = XLSX.utils.encode_cell({
    r: Math.max(data.length - 1, 0),
    c: Math.max(headers.length - 1, 0),
  });

  worksheet['!cols'] = headers.map((header) => ({ wch: getExcelColumnWidth(header, rows) }));
  worksheet['!rows'] = data.map((_, index) => ({ hpt: index === 0 ? 34 : 22 }));

  if (headers.length > 0) {
    worksheet['!autofilter'] = { ref: `A1:${lastCellAddress}` };
  }

  data.forEach((row, rowIndex) => {
    row.forEach((_, columnIndex) => {
      const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      if (!worksheet[cellAddress]) return;
      let rowStyle = EXCEL_BODY_STYLE;
      if (rowIndex === 0) {
        rowStyle = EXCEL_HEADER_STYLE;
      } else if (rowIndex % 2 === 0) {
        rowStyle = EXCEL_ALT_ROW_STYLE;
      }
      worksheet[cellAddress].s = rowStyle;
    });
  });

  return worksheet;
};

const sanitizeSheetName = (name, fallback) => {
  const safe = `${name || fallback || 'Relatório'}`.replace(/[\\/?*[\]:]/g, '-').trim();
  return safe.length > 31 ? safe.slice(0, 31).trim() : safe;
};

const exportRowsToExcel = (sheets, fileName) => {
  if (!Array.isArray(sheets) || sheets.length === 0) return false;
  const workbook = XLSX.utils.book_new();
  const usedNames = new Set();

  sheets.forEach((sheet, index) => {
    const rows = Array.isArray(sheet?.rows) ? sheet.rows : [];
    const columns = Array.isArray(sheet?.columns) ? sheet.columns : [];
    const worksheet = buildStyledWorksheet(rows, columns);
    let baseName = sanitizeSheetName(sheet?.name, `Relatório ${index + 1}`);
    let finalName = baseName;
    let dedupCounter = 2;
    while (usedNames.has(finalName)) {
      const suffix = ` (${dedupCounter})`;
      finalName = `${baseName.slice(0, 31 - suffix.length)}${suffix}`;
      dedupCounter += 1;
    }
    usedNames.add(finalName);
    XLSX.utils.book_append_sheet(workbook, worksheet, finalName);
  });

  XLSX.writeFile(workbook, `${sanitizeFilename(fileName)}.xlsx`, { cellStyles: true });
  return true;
};

const PDF_COLORS = {
  primary: [47, 107, 88],
  accent: [200, 75, 75],
  accentYellow: [218, 185, 80],
  accentBlue: [135, 206, 220],
  text: [33, 37, 41],
  textLight: [108, 117, 125],
};
const PDF_MARGIN = { left: 20, right: 20, bottom: 35 };
const PDF_PAGE_W = 210;
const PDF_CONTENT_W = PDF_PAGE_W - PDF_MARGIN.left - PDF_MARGIN.right;

const PDF_HEADER_IMG_H = 44;

const drawPdfChrome = (doc, isFirstPage) => {
  const ph = doc.internal.pageSize.getHeight();
  doc.addImage(SINDILEGIS_LOGO, 'PNG', 0, 0, PDF_PAGE_W, PDF_HEADER_IMG_H);
  if (isFirstPage) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PDF_COLORS.text);
    doc.text('Ficha de Inscrição Copa/Sarau Sindilegis', PDF_PAGE_W / 2, PDF_HEADER_IMG_H + 8, {
      align: 'center',
    });
  }
  const fy = ph - 22;
  const sw = PDF_CONTENT_W / 4;
  doc.setLineWidth(1);
  [PDF_COLORS.primary, PDF_COLORS.accentYellow, PDF_COLORS.accent, PDF_COLORS.accentBlue].forEach(
    (c, i) => {
      doc.setDrawColor(...c);
      doc.line(PDF_MARGIN.left + sw * i, fy, PDF_MARGIN.left + sw * (i + 1), fy);
    }
  );
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PDF_COLORS.text);
  doc.text(
    'Sindicato dos Servidores do Poder Legislativo Federal e Tribunal de Contas da União',
    PDF_PAGE_W / 2,
    fy + 5,
    { align: 'center' }
  );
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...PDF_COLORS.textLight);
  doc.text(
    'SGAS 610, conjunto C, módulo 70 | CEP: 70.200-700 - Asa Sul - L2, Brasília/DF | (61) 3214-7300',
    PDF_PAGE_W / 2,
    fy + 10,
    { align: 'center' }
  );
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PDF_COLORS.primary);
  doc.text('www.sindilegis.org.br', PDF_PAGE_W / 2, fy + 15, { align: 'center' });
};

const pdfPage = (doc, y, need) => {
  if (y + need > doc.internal.pageSize.getHeight() - PDF_MARGIN.bottom) {
    doc.addPage();
    drawPdfChrome(doc, false);
    return PDF_HEADER_IMG_H + 4;
  }
  return y;
};

const pdfSection = (doc, y, title) => {
  const ny = pdfPage(doc, y, 14);
  doc.setFillColor(...PDF_COLORS.primary);
  doc.rect(PDF_MARGIN.left, ny - 4, PDF_CONTENT_W, 8, 'F');
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text(title, PDF_MARGIN.left + 3, ny + 1.5);
  return ny + 10;
};

const pdfField = (doc, y, label, value) => {
  const ny = pdfPage(doc, y, 7);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PDF_COLORS.text);
  doc.text(`${label}:`, PDF_MARGIN.left + 2, ny);
  doc.setFont('helvetica', 'normal');
  const v = value === null || value === undefined || value === '' ? '\u2014' : `${value}`;
  doc.text(v, PDF_MARGIN.left + 52, ny);
  return ny + 6;
};

const pdfBullet = (doc, y, text) => {
  const ny = pdfPage(doc, y, 6);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...PDF_COLORS.text);
  doc.text(`\u2022 ${text}`, PDF_MARGIN.left + 4, ny);
  return ny + 5.5;
};

const pdfSubLabel = (doc, y, title) => {
  const ny = pdfPage(doc, y, 7);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...PDF_COLORS.primary);
  doc.text(title, PDF_MARGIN.left + 2, ny);
  return ny + 6;
};

const buildRegistrationSheetPdf = (registration) => {
  const doc = new JsPDF();
  const companions = registration.companions || [];
  drawPdfChrome(doc, true);
  let y = PDF_HEADER_IMG_H + 14;

  y = pdfSection(doc, y, 'FILIADO');
  y = pdfField(doc, y, 'Nome', registration.name);
  y = pdfField(doc, y, 'CPF', registration.cpf);
  y = pdfField(doc, y, 'Data de Nascimento', formatDate(parseBirthday(registration.birthday)));
  y = pdfField(doc, y, 'Cidade', registration.city);
  y = pdfField(doc, y, 'Estado', registration.state);
  y = pdfField(doc, y, 'E-mail', registration.email);
  y = pdfField(doc, y, 'Celular', registration.phone);
  y = pdfField(doc, y, 'Situação', registration.situation);
  y = pdfField(doc, y, 'Matrícula', registration.matricula);
  y = pdfField(doc, y, 'Órgão de lotação', registration.organ);

  const customAnswers = Array.isArray(registration.custom_answers)
    ? registration.custom_answers
    : [];
  if (customAnswers.length > 0) {
    y += 2;
    y = pdfSubLabel(doc, y, 'Informações adicionais');
    customAnswers.forEach((entry) => {
      const label = entry.questionText || '';
      const answer = Array.isArray(entry.answer) ? entry.answer.join(', ') : entry.answer;
      y = pdfField(doc, y, label, answer || '');
    });
  }
  y += 4;

  y = pdfSection(doc, y, 'MODALIDADES \u2014 COPA SINDILEGIS');
  const sportsItems = getSportsSummary(registration.modalities, registration.birthday);
  if (sportsItems.length === 0) {
    y = pdfBullet(doc, y, 'Não irá participar');
  } else {
    sportsItems.forEach((item) => {
      y = pdfBullet(doc, y, item);
    });
  }
  y += 4;

  y = pdfSection(doc, y, 'MODALIDADES \u2014 SARAU SINDILEGIS');
  const sarauItems = getSarauSummary(registration.sarau_activities);
  if (sarauItems.length === 0) {
    y = pdfBullet(doc, y, 'Não irá participar');
  } else {
    sarauItems.forEach((item) => {
      y = pdfBullet(doc, y, item);
    });
  }
  y += 4;

  y = pdfSection(doc, y, 'NÚMERO DE ACOMPANHANTES');
  y = pdfField(doc, y, 'Acompanhantes pagantes', registration.companions_paying_count || 0);
  y = pdfField(doc, y, 'Acompanhantes não pagantes', registration.companions_free_count || 0);
  y += 4;

  y = pdfSection(doc, y, 'VALORES');
  y = pdfField(
    doc,
    y,
    `${registration.companions_paying_count || 0} acompanhante(s)`,
    formatCurrency(registration.companion_total) || 'R$ 0,00'
  );
  y = pdfField(
    doc,
    y,
    `${registration.extra_rooms_count || 0} quarto(s)`,
    formatCurrency(registration.room_total) || 'R$ 0,00'
  );
  y = pdfField(doc, y, 'Valor total', formatCurrency(registration.total_amount) || 'Isento');
  y += 4;

  companions.forEach((companion, index) => {
    const roomLabel =
      companion.room_number === 1
        ? 'Quarto do filiado'
        : `Quarto adicional ${companion.room_number - 1}`;
    y = pdfSection(doc, y, `DADOS ACOMPANHANTE \u2014 ${index + 1} (${roomLabel})`);
    y = pdfField(doc, y, 'Nome', companion.name);
    y = pdfField(doc, y, 'CPF', companion.cpf);
    y = pdfField(doc, y, 'Data de Nascimento', formatDate(parseBirthday(companion.birthday)));
    y = pdfField(doc, y, 'Cidade', companion.city);
    y = pdfField(doc, y, 'Estado', companion.state);
    y = pdfField(doc, y, 'E-mail', companion.email);
    y = pdfField(doc, y, 'Celular', companion.phone);
    y += 2;

    y = pdfSubLabel(doc, y, 'MODALIDADES \u2014 COPA SINDILEGIS');
    const compSports = getSportsSummary(companion.modalities, companion.birthday);
    if (compSports.length === 0) {
      y = pdfBullet(doc, y, 'Não irá participar');
    } else {
      compSports.forEach((item) => {
        y = pdfBullet(doc, y, item);
      });
    }
    y += 2;

    y = pdfSubLabel(doc, y, 'MODALIDADES \u2014 SARAU SINDILEGIS');
    const compSarau = getSarauSummary(companion.sarau_activities);
    if (compSarau.length === 0) {
      y = pdfBullet(doc, y, 'Não irá participar');
    } else {
      compSarau.forEach((item) => {
        y = pdfBullet(doc, y, item);
      });
    }
    y += 6;
  });

  y = pdfSection(doc, y, 'TERMOS E AUTORIZAÇÕES');
  buildTermsSummary(registration.terms_accepted, companions).forEach((row) => {
    y = pdfBullet(doc, y, `${row.field}: ${row.value || '\u2014'}`);
  });

  return doc;
};

function CopaSarauDashboard(props) {
  const classes = useStyles();
  const [inscriptions, setInscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloadingSheetId, setDownloadingSheetId] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [reportType, setReportType] = useState('geral');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    sports: 0,
    sarau: 0,
    rooms: 0,
  });

  const stationId = props.auth?.user?.establishments?.[0]?.stations?.[0]?.id;
  const [activityOptions, setActivityOptions] = useState([
    { value: 'all', label: 'Todas as atividades' },
  ]);
  const [selectedActionId, setSelectedActionId] = useState(
    props.location?.query?.action_id || 'all'
  );
  const [hotelOptions, setHotelOptions] = useState([{ value: 'all', label: 'Todos os hotéis' }]);
  const [selectedHotelId, setSelectedHotelId] = useState('all');

  useEffect(() => {
    if (stationId && stationId !== 1) {
      props.router.push('/owner');
    }
  }, [stationId, props.router]);

  useEffect(() => {
    if (!stationId) return;
    axios
      .get(`${BASE_API}/copa-sarau/activities`, {
        headers: getHeaders(),
        params: { station_id: stationId },
      })
      .then((response) => {
        const list = response.data?.activities || [];
        setActivityOptions([
          { value: 'all', label: 'Todas as atividades' },
          ...list.map((a) => ({
            value: a.id,
            label: a.name || `Atividade ${a.id}`,
          })),
        ]);
      })
      .catch((error) => {
        console.error('Erro ao carregar atividades Copa/Sarau:', error);
        setActivityOptions([{ value: 'all', label: 'Todas as atividades' }]);
      });
  }, [stationId]);

  useEffect(() => {
    setSelectedHotelId('all');
    if (!stationId || selectedActionId === 'all') {
      setHotelOptions([{ value: 'all', label: 'Todos os hotéis' }]);
      return;
    }
    axios
      .get(`${BASE_API}/copa-sarau/activity-hotels`, {
        headers: getHeaders(),
        params: { station_id: stationId, action_id: selectedActionId },
      })
      .then((response) => {
        const hotels = response.data?.hotels || [];
        setHotelOptions([
          { value: 'all', label: 'Todos os hotéis' },
          ...hotels.map((h) => ({ value: h.id, label: h.name })),
        ]);
      })
      .catch((error) => {
        console.error('Erro ao carregar hotéis Copa/Sarau:', error);
        setHotelOptions([{ value: 'all', label: 'Todos os hotéis' }]);
      });
  }, [stationId, selectedActionId]);

  const fetchData = useCallback(async () => {
    if (!stationId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${BASE_API}/copa-sarau/inscricoes`, {
        headers: getHeaders(),
        params: {
          station_id: stationId,
          action_id: selectedActionId === 'all' ? undefined : selectedActionId,
          hotel_id: selectedHotelId === 'all' ? undefined : selectedHotelId,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          search,
          status: statusFilter,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
      });
      setInscriptions(response.data?.rows || []);
      setTotalRecords(response.data?.count || 0);
    } catch (error) {
      console.error('Erro ao buscar inscrições Copa/Sarau:', error);
      toastr.error('Erro', 'Não foi possível carregar as inscrições da Copa/Sarau.');
      setInscriptions([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    stationId,
    selectedActionId,
    selectedHotelId,
    rowsPerPage,
    page,
    search,
    statusFilter,
    startDate,
    endDate,
  ]);

  const fetchStats = useCallback(async () => {
    if (!stationId) return;
    try {
      const response = await axios.get(`${BASE_API}/copa-sarau/stats`, {
        headers: getHeaders(),
        params: {
          station_id: stationId,
          action_id: selectedActionId === 'all' ? undefined : selectedActionId,
          hotel_id: selectedHotelId === 'all' ? undefined : selectedHotelId,
          status: statusFilter,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
      });
      setStats(response.data || { total: 0, sports: 0, sarau: 0, rooms: 0 });
    } catch (error) {
      console.error('Erro ao buscar estatísticas:', error);
      setStats({ total: 0, sports: 0, sarau: 0, rooms: 0 });
    }
  }, [stationId, selectedActionId, selectedHotelId, statusFilter, startDate, endDate]);

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [fetchData, fetchStats]);

  const handleDownloadReport = async () => {
    if (!stationId) return;
    try {
      setLoadingReport(true);
      const response = await axios.get(`${BASE_API}/copa-sarau/report`, {
        headers: getHeaders(),
        params: {
          station_id: stationId,
          action_id: selectedActionId === 'all' ? undefined : selectedActionId,
          hotel_id: selectedHotelId === 'all' ? undefined : selectedHotelId,
          report_type: reportType,
          status: statusFilter,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
        },
      });

      const sheets = Array.isArray(response.data?.sheets)
        ? response.data.sheets
        : [
            {
              name: 'Relatório',
              rows: response.data?.rows || [],
              columns: response.data?.rows?.[0] ? Object.keys(response.data.rows[0]) : [],
            },
          ];

      const hasData = sheets.some((sheet) => Array.isArray(sheet?.rows) && sheet.rows.length > 0);

      if (!hasData) {
        toastr.warning('Sem dados', 'Nenhum registro encontrado para os filtros selecionados.');
        return;
      }

      exportRowsToExcel(sheets, response.data?.filename || `copa-sarau-${reportType}`);
      toastr.success('Sucesso', 'Relatório exportado com sucesso.');
    } catch (error) {
      console.error('Erro ao exportar relatório Copa/Sarau:', error);
      toastr.error('Erro', 'Não foi possível exportar o relatório.');
    } finally {
      setLoadingReport(false);
    }
  };

  const handleDownloadRegistrationSheet = async (registrationId) => {
    if (!stationId) return;
    try {
      setDownloadingSheetId(registrationId);
      const response = await axios.get(
        `${BASE_API}/copa-sarau/registration-sheet/${registrationId}`,
        {
          headers: getHeaders(),
          params: {
            station_id: stationId,
          },
        }
      );

      const registration = response.data?.registration;
      if (!registration) {
        toastr.warning('Atenção', 'Não foi possível localizar a ficha de inscrição.');
        return;
      }

      const doc = buildRegistrationSheetPdf(registration);
      doc.save(`${sanitizeFilename(`ficha-inscricao-${registration.name}`)}.pdf`);
    } catch (error) {
      console.error('Erro ao baixar ficha de inscrição:', error);
      toastr.error('Erro', 'Não foi possível baixar a ficha de inscrição.');
    } finally {
      setDownloadingSheetId(null);
    }
  };

  return (
    <GeneralGrid container justifyContent="center" className={classes.container}>
      <GeneralGrid item xs={12}>
        <Box className={classes.header}>
          <Typography className={classes.title}>Copa/Sarau Sindilegis</Typography>
          <Typography className={classes.subtitle}>
            Acompanhamento de inscrições, atividades e relatórios do evento
          </Typography>
        </Box>

        <Paper variant="outlined" className={classes.filtersCard}>
          <Box className={classes.sectionHeader}>
            <Typography style={{ fontSize: 22, fontWeight: 700, color: '#1a3a5c' }}>
              Download de Relatórios
            </Typography>
            <Typography style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
              Selecione o modelo, o período e o status para gerar o relatório em Excel.
            </Typography>
          </Box>

          <Box className={classes.filtersGrid}>
            <TextField
              select
              label="Tipo de relatório"
              variant="outlined"
              size="small"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}>
              {REPORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Atividade"
              variant="outlined"
              size="small"
              value={selectedActionId}
              onChange={(e) => {
                setSelectedActionId(e.target.value);
                setPage(0);
              }}>
              {activityOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Hotel"
              variant="outlined"
              size="small"
              value={selectedHotelId}
              disabled={selectedActionId === 'all' || hotelOptions.length <= 1}
              onChange={(e) => {
                setSelectedHotelId(e.target.value);
                setPage(0);
              }}>
              {hotelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Data início"
              type="date"
              variant="outlined"
              size="small"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Data fim"
              type="date"
              variant="outlined"
              size="small"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(0);
              }}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              select
              label="Status de seleção"
              variant="outlined"
              size="small"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="contained"
              className={classes.actionButton}
              onClick={handleDownloadReport}
              disabled={loadingReport}
              startIcon={<GetAppIcon />}
              fullWidth>
              {loadingReport ? 'Gerando relatório...' : 'Baixar Excel'}
            </Button>
          </Box>
        </Paper>

        <Box className={classes.statsContainer}>
          <Paper className={classes.statCard} elevation={0} variant="outlined">
            <Box className={classes.statIcon} style={{ backgroundColor: '#e3f2fd' }}>
              <PeopleOutlineIcon style={{ color: '#1565c0', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography className={classes.statValue}>{stats.total || 0}</Typography>
              <Typography className={classes.statLabel}>Inscritos</Typography>
            </Box>
          </Paper>
          <Paper className={classes.statCard} elevation={0} variant="outlined">
            <Box className={classes.statIcon} style={{ backgroundColor: '#e8f5e9' }}>
              <SportsIcon style={{ color: '#2e7d32', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography className={classes.statValue}>{stats.sports || 0}</Typography>
              <Typography className={classes.statLabel}>Inscrições Esportivas</Typography>
            </Box>
          </Paper>
          <Paper className={classes.statCard} elevation={0} variant="outlined">
            <Box className={classes.statIcon} style={{ backgroundColor: '#fce4ec' }}>
              <LibraryMusicIcon style={{ color: '#c62828', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography className={classes.statValue}>{stats.sarau || 0}</Typography>
              <Typography className={classes.statLabel}>Inscrições Sarau</Typography>
            </Box>
          </Paper>
          <Paper className={classes.statCard} elevation={0} variant="outlined">
            <Box className={classes.statIcon} style={{ backgroundColor: '#fff3e0' }}>
              <HotelIcon style={{ color: '#e65100', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography className={classes.statValue}>{stats.rooms || 0}</Typography>
              <Typography className={classes.statLabel}>Quartos</Typography>
            </Box>
          </Paper>
        </Box>

        <Box className={classes.sectionHeader}>
          <Typography style={{ fontSize: 20, fontWeight: 700, color: '#1a3a5c' }}>
            Baixar ficha de inscrição
          </Typography>
          <Typography style={{ color: '#666', fontSize: 13, marginTop: 4 }}>
            Pesquise pelo nome, CPF ou e-mail do inscrito e gere a ficha individual em PDF.
          </Typography>
        </Box>

        <Box className={classes.searchBox}>
          <TextField
            variant="outlined"
            size="small"
            placeholder="Selecione o nome ou CPF do inscrito"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            style={{ flex: 1, maxWidth: 480 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <LoadingProgress />
          </Box>
        ) : inscriptions.length === 0 ? (
          <Paper variant="outlined" style={{ borderRadius: 12 }}>
            <Box className={classes.emptyState}>
              <PeopleOutlineIcon className={classes.emptyIcon} />
              <Typography className={classes.emptyText}>Nenhuma inscrição encontrada</Typography>
              <Typography className={classes.emptySubtext}>
                As inscrições aparecerão aqui conforme os participantes preencherem o formulário
              </Typography>
            </Box>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined" style={{ borderRadius: 12 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Atividade</TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>Inscrição</TableCell>
                  <TableCell>Modalidades</TableCell>
                  <TableCell>Acompanhantes</TableCell>
                  <TableCell>Valor</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inscriptions.map((row) => {
                  const statusStyle = getStatusChipStyle(row.status);
                  return (
                    <TableRow key={row.id}>
                      <TableCell>{row.action_name || '-'}</TableCell>
                      <TableCell>{row.name || '-'}</TableCell>
                      <TableCell>{row.cpf || '-'}</TableCell>
                      <TableCell>{row.email || '-'}</TableCell>
                      <TableCell>{formatDate(row.created_at, true) || '-'}</TableCell>
                      <TableCell>{row.sports_count || 0}</TableCell>
                      <TableCell>{row.companions_count || 0}</TableCell>
                      <TableCell>
                        {Number(row.total_amount || 0) > 0 ? (
                          <Chip
                            label={formatCurrency(row.total_amount)}
                            className={classes.chipPagante}
                            size="small"
                          />
                        ) : (
                          <Chip label="Gratuito" className={classes.chipGratuito} size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(row.status)}
                          size="small"
                          style={{
                            ...statusStyle,
                            fontSize: 11,
                            height: 24,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Baixar ficha de inscrição">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadRegistrationSheet(row.id)}
                              disabled={downloadingSheetId === row.id}>
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas por página"
            />
          </TableContainer>
        )}
      </GeneralGrid>
    </GeneralGrid>
  );
}

CopaSarauDashboard.propTypes = {
  auth: PropTypes.shape({
    user: PropTypes.shape({
      establishments: PropTypes.array,
    }),
  }),
  location: PropTypes.shape({
    query: PropTypes.shape({
      action_id: PropTypes.any,
    }),
  }),
  router: PropTypes.shape({
    push: PropTypes.func,
  }),
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(CopaSarauDashboard);
